import { Alert, Box, Card, Divider, Snackbar, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PreOrderData, PreOrderValidationError, ToastNotification } from "../../common/Interfaces";
import { useEffect, useState } from "react";
import { generateClearPreOrder, generateClearPreOrderValidationErrors } from "../../common/EntitiesManager";
import { createEmailNotification } from "../../common/requests/EmailNotificationRequests";
import { createPreOrder } from "../../common/requests/PreOrdersRequests";
import { ToastType } from "../../common/requests/Enums";
import { useNavigate } from "react-router-dom";
import styles from '../clientPreorder/ClientPreOrder.module.scss';
import BasicModal from "../../components/common/basicModal/BasicModal";
import { getAlertType } from "../../common/Utils";
import TitleSubtitle from "../../components/common/formControls/titleSubtitle/TitleSubtitle";
import ClientData from "../../components/clientData/ClientData";
import InputTextFieldMultiline from "../../components/common/formControls/textField/InputTextFieldMultiline";
import SuccessButton from "../../components/common/formControls/buttons/SuccessButton";
import Combobox from "../../components/common/formControls/combobox/Combobox";
import AppLogo from "../../components/common/appLogo/AppLogo";

export default function ClientPreOrder () {
    const {t} = useTranslation();

    const productTypes = [
        /*{
            Id: 11,
            Name: t("AIR_CONDITIONER"),
            Type: ["INSTALL", "MAINTENANCE", "REVIEW"]
        },*/
        {
            Id: 12,
            Name: t("LAUNDRY"),
            Type: ["INSTALL", "MAINTENANCE", "REVIEW"]
        },
        /*{
            Id: 8,
            Name: t("FREEZER"),
            Type: ["INSTALL", "MAINTENANCE", "REVIEW"]
        },*/
        {
            Id: 35,
            Name: t("CCTV"),
            Type: ["INSTALL", "MAINTENANCE"]
        },
        {
            Id: 7,
            Name: t("TV"),
            Type: ["MAINTENANCE", "REVIEW"]
        },
        {
            Id: 10,
            Name: t("AUDIO"),
            Type: ["MAINTENANCE", "REVIEW"]
        },
        {
            Id: 17,
            Name: t("ROBOT_VACCUM_CLEANER"),
            Type: ["MAINTENANCE", "REVIEW"]
        },
    ];

    const navigate = useNavigate();
    const [preOrder, setPreOrder] = useState<PreOrderData>(generateClearPreOrder());
    const [preOrderValidations, setPreOrderValidations] = useState<PreOrderValidationError>(generateClearPreOrderValidationErrors());
    const [toastInfo, setToastInfo] = useState<ToastNotification|undefined>(undefined);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [selectedServiceType, setSelectedServiceType] = useState<any|undefined> (undefined);
    const [selectedProductType, setSelectedProductType] = useState<any|undefined> (undefined);
    const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
    const [productTypesFiltered, setProductTypesFiltered] = useState(productTypes);
    const maximumCharacters = 10000;

    const serviceTypes = [
        {
            Id: "INSTALL",
            Name: t("INSTALL")
        },
        {
            Id: "REVIEW",
            Name: t("REVIEW")
        },
        {
            Id: "MAINTENANCE",
            Name: t("MAINTENANCE")
        }
    ]

    useEffect(() => {
        if (selectedServiceType){
            const filteredProducts = productTypes.filter((x) => x.Type.includes(selectedServiceType.Id));
            setProductTypesFiltered(filteredProducts);

            return;
        }

        setSelectedProductType(undefined);
    }, [selectedServiceType]);

    const handleObservationChange = (e: any) => {
        setPreOrder({
            ...preOrder,
            Observation: e.target.value
        });
    }

    const handleEmailNotification = async (preOrderToNotify: any, preOrderCode?: string) => {
        try{
            const creationResult = await createEmailNotification(preOrderToNotify, preOrderCode ?? '',
                process.env.REACT_APP_PRE_ORDER_TOKEN ?? '', 
                process.env.REACT_APP_PRE_ORDER_MANAGEMENT_ID ?? '', 
                parseInt(process.env.REACT_APP_PRE_ORDER_COMPANY_ID ?? '0'),
                process.env.REACT_APP_PRE_ORDER_USER ?? '');
            
            console.log(JSON.stringify(creationResult));
        }   
        catch(error: any){
            console.error(error.message);
        }
    }

    const validatePreOrder = ():boolean =>{
        if (!selectedServiceType) {
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ServiceType: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!selectedProductType){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ProductType: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ClientIdentificationType){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientIdentificationType: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ClientIdentification || preOrder.ClientIdentification.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientIdentification: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ClientName || preOrder.ClientName.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientName: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.State || preOrder.State.Id === "0"){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientState: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.City || preOrder.City.Id === "0"){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientCity: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ClientAddress || preOrder.ClientAddress.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientAddress: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.ClientPhoneNumber || preOrder.ClientPhoneNumber.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                ClientPhoneNumber: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        if(!preOrder.Observation || preOrder.Observation.trim().length === 0){
            setPreOrderValidations({
                ...generateClearPreOrderValidationErrors(),
                Observation: t("EMPTYFIELDERROR"),
            });
            return false;
        }

        setPreOrderValidations(generateClearPreOrderValidationErrors());
        
        return true;
    }

    const handleCreatePreOrder = async () => {
        if(!!validatePreOrder()){
            setIsProcessing(true);
            setToastInfo({
                type: ToastType.info,
                message: t("CREATINGPREORDER"),
            });

            const creationResult = await createPreOrder({
                ...preOrder,
                ProductLocation: 'PRODUCTO SE ENCUENTRA EN DOMICILIO DEL CLIENTE',
                ProductType: {...selectedProductType},
                ProductBrand: {Id: "65", Name: "Solicitud de cliente"},
                Observation: `${t("SERVICE_TYPE")}: ${selectedServiceType.Name}\n${t("PRODUCTTYPE")}: ${selectedProductType.Name}\n${t("OBSERVATIONS")}: ${preOrder.Observation}`
            }, 
                                                        process.env.REACT_APP_PRE_ORDER_TOKEN ?? '', 
                                                        process.env.REACT_APP_PRE_ORDER_MANAGEMENT_ID ?? '', 
                                                        parseInt(process.env.REACT_APP_PRE_ORDER_COMPANY_ID ?? '0'),
                                                        process.env.REACT_APP_PRE_ORDER_USER ?? '');
            if (!!creationResult.Ok){
                handleEmailNotification(preOrder, creationResult.data);
                setToastInfo({
                    type: ToastType.info,
                    message: t("PREORDERCREATED"),
                });

                setIsProcessing(false);
                navigate("/svc/success");
                return;
            }

            setToastInfo({
                type: ToastType.error,
                message: creationResult.data ? `${t("UNEXPECTEDERROR")}: ${JSON.stringify(creationResult.data)}` : t("UNEXPECTEDERROR"),
            })

            setIsProcessing(false);
            return;
        }

        setToastInfo({
            type: ToastType.error,
            message: t("THEREAREFIELDWITHERRORS"),
        });
    }

    const handleServiceTypeChange = (_e: any, value: any) => {
        setSelectedServiceType(value);
    }

    const handleProductTypeChange = (_e: any, value: any) => {
        setSelectedProductType(value);
    }

    return <Box className={styles.container}>
        <Box className={styles.internalContainerColumn}>
            <BasicModal
                openModal={!!openConfirmationModal}
                setOpenModal={setOpenConfirmationModal}
                title={t("CONFIRMSAVE")}
                subTitle={t("QUESTIONWANTTOCREATESERVICE")}
                successButton={{
                    id: 'confirmationButton',
                    label: t("YES"),
                    onClick: () => {
                        setOpenConfirmationModal(false);
                        handleCreatePreOrder();
                    }
                }}
                cancelButton={{
                    id: 'CancelButton',
                    label: t("CANCEL"),
                    onClick: () => {setOpenConfirmationModal(false)}
                }}
            />

            <Snackbar
                sx={{minWidth: "300px", maxWidth: "80%"}}
                open={!!toastInfo}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                autoHideDuration={3000}
                onClose={() => {
                    setToastInfo(undefined);
                }}
            >
                <Alert severity={getAlertType(toastInfo?.type ?? ToastType.info)}>
                    <Typography>
                        {toastInfo?.message}
                    </Typography>
                </Alert>
            </Snackbar>

            <Box className={styles.internalContainer}>
                <Card>
                    <Box className={styles.verticalContainer}>
                        <AppLogo/>
                        
                        <TitleSubtitle title={`${t("NEW")} ${t("SERVICE")}`}
                            subTitle={`${t("SERVICE_PAGE_SUBTITLE")}`}/>

                        <Combobox fieldId={"ServiceType"} 
                            handleValueChange={handleServiceTypeChange} 
                            fieldValue={selectedServiceType ?? {Id: "0", Name: ""}} 
                            fieldOptions={serviceTypes}
                            fieldLabel={t("SERVICE_TYPE")}
                            errorMessage={preOrderValidations.ServiceType}
                            width={"100%"}/>
                        
                        <Combobox fieldId={"ProductType"} 
                            handleValueChange={handleProductTypeChange} 
                            fieldValue={selectedProductType ?? {Id: "0", Name: ""}} 
                            fieldOptions={productTypesFiltered}
                            fieldLabel={t("PRODUCTTYPE")}
                            errorMessage={preOrderValidations.ProductType}
                            disabled={!selectedServiceType}
                            width={"100%"}/>
                            
                    </Box>
                </Card>

                {selectedServiceType && selectedProductType && (<ClientData disabled={!selectedServiceType} preOrder={preOrder} setPreOrder={setPreOrder} preOrderValidations={preOrderValidations} auth={{
                    isAuthenticated: true,
                    token: process.env.REACT_APP_PRE_ORDER_TOKEN,
                    sessionData: {
                        managementId: process.env.REACT_APP_PRE_ORDER_MANAGEMENT_ID
                    }
                }}/>)}

                {selectedServiceType && selectedProductType && (<Card>
                    <Box className={styles.verticalContainer}>
                        <TitleSubtitle title={t("REQUESTDETAIL")} subTitle={t("SERVICE_DETAIL_SUBTITLE")}/>
                        <Divider/>
                        <InputTextFieldMultiline id={"Observation"} 
                            maximumCharacters={maximumCharacters} 
                            label={""} 
                            errorMessage={preOrderValidations.Observation}
                            value={preOrder.Observation}
                            isReadOnly={preOrder.Id > 0}
                            handleValueChange={handleObservationChange}/>
                    </Box>
                </Card>)}

                <Box className={styles.titleContainer}>
                    <p className={styles.subtitle}>En cumplimiento de lo previsto en la ley 1581 de 2012, sus reglamentos y demás normas concordantes, los datos aquí suministrados serán tratados de acuerdo con las políticas de tratamiento de datos personales habeas data, la cual podrá visualizar en <a href='https://www.centecaribe.com.co/politica-privacidad.html' target="_blank">https://www.centecaribe.com.co/politica-privacidad.html</a> si usted continúa con la solicitud de servicio, daremos por entendido su aprobación.</p>
                </Box>

                <Box className={styles.footerButtonsContainer}>
                    <SuccessButton id={"CreatePreorderButton"} label={`${t("CREATE")} ${t("SERVICE")}`} onclickHandler={() => {setOpenConfirmationModal(true)}} disabled={isProcessing}/>
                </Box>
            </Box>
        </Box>
    </Box>
}